import React from 'react';
import axios from 'axios';
import axiosBetterStacktrace from 'axios-better-stacktrace';
import { useSelector } from 'react-redux';
import { createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from 'react-router-dom';
import moment from 'moment';
import momentDurationFormatSetup from 'moment-duration-format';
import { LicenseInfo } from '@mui/x-license';
import { init as SentryInit, reactRouterV6BrowserTracingIntegration, replayIntegration } from '@sentry/react';

import rtlPlugin from 'stylis-plugin-rtl';
import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';

import BrowserRouter from '@fuse/core/BrowserRouter';
import FuseAuthorization from '@fuse/core/FuseAuthorization';
import FuseLayout from '@fuse/core/FuseLayout';
import FuseTheme from '@fuse/core/FuseTheme';

import { selectCurrentLanguageDirection } from 'app/store/i18nSlice';
import withAppProviders from './withAppProviders';
import { AuthProvider } from './auth';

// axios.defaults.baseURL = 'http://localhost:5000';
axios.defaults.baseURL = import.meta.env.VITE_MIDDLEWHERE_ENDPOINT;

const emotionCacheOptions = {
	rtl: {
		key: 'muirtl',
		stylisPlugins: [rtlPlugin],
		insertionPoint: document.getElementById('emotion-insertion-point') ?? undefined
	},
	ltr: {
		key: 'muiltr',
		stylisPlugins: [],
		insertionPoint: document.getElementById('emotion-insertion-point') ?? undefined
	}
};

// Register plugin to get better stacktrace for axios
axiosBetterStacktrace(axios);

// Register moment-duration-format to moment
momentDurationFormatSetup(moment);

// Register MUI license
LicenseInfo.setLicenseKey(import.meta.env.VITE_MUI_LICENSE);

SentryInit({
	dsn: import.meta.env.PROD ? import.meta.env.VITE_SENTRY_DSN : undefined,
	environment: import.meta.env.MODE,
	integrations: [
		// See docs for support of different versions of variation of react router
		// https://docs.sentry.io/platforms/javascript/guides/react/configuration/integrations/react-router/
		reactRouterV6BrowserTracingIntegration({
			useEffect: React.useEffect,
			useLocation,
			useNavigationType,
			createRoutesFromChildren,
			matchRoutes
		}),
		replayIntegration({
			networkDetailAllowUrls: [`${axios.defaults.baseURL}/api/`],
			maskAllText: false,
			blockAllMedia: false
		})
	],

	// Set tracesSampleRate to 1.0 to capture 100%
	// of transactions for performance monitoring.
	tracesSampleRate: 1.0,

	// Set `tracePropagationTargets` to control for which URLs distributed tracing should be enabled
	tracePropagationTargets: ['localhost', /^https:\/\/yourserver\.io\/api/],

	// Capture Replay for 10% of all sessions,
	// plus for 100% of sessions with an error
	replaysSessionSampleRate: 0.1,
	replaysOnErrorSampleRate: 1.0,

	sendDefaultPii: true
});

function App() {
	const langDirection = useSelector(selectCurrentLanguageDirection);

	return (
		<CacheProvider value={createCache({ ...emotionCacheOptions[langDirection], prepend: true })}>
			<AuthProvider>
				<BrowserRouter>
					<FuseAuthorization>
						<FuseTheme>
							{/* <SnackbarProvider
								maxSnack={5}
								anchorOrigin={{
									vertical: 'top',
									horizontal: 'right'
								}}
								classes={{
									containerRoot: 'top-0 right-0 mt-52 md:mt-68 mr-8 z-99'
								}}
							> */}
							<FuseLayout />
							{/* </SnackbarProvider> */}
						</FuseTheme>
					</FuseAuthorization>
				</BrowserRouter>
			</AuthProvider>
		</CacheProvider>
	);
}

export default withAppProviders(App)({});
